import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cashApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { useDrawer } from '../../contexts/DrawerContext';
import { carriedWords } from '../../lib/carriedWords';
import { ManagerStepUpFields, useManagerStepUp, cashError } from './ManagerStepUp';

/**
 * Open the till: confirm the float and the drawer is live. The float defaults to what last
 * night's close carried, else the venue's float (the server decides; this shows it).
 */
const OpenTillDialog = ({ open, onOpenChange, onOpened, formatCurrency }) => {
  const { user } = useAuth();
  const { drawer, canManage, refresh } = useDrawer();
  const defaultFloat = drawer?.last_closed?.float_carried ?? drawer?.settings?.float ?? 100;
  const carried = carriedWords(drawer?.last_closed, formatCurrency);
  const [floatOpening, setFloatOpening] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const stepUp = useManagerStepUp({ open, needed: !canManage, siteId: user?.primary_site });

  useEffect(() => { if (open) setFloatOpening(Number(defaultFloat).toFixed(2)); }, [open, defaultFloat]);

  const amount = parseFloat(floatOpening);
  const canSubmit = !submitting && Number.isFinite(amount) && amount >= 0 && stepUp.ready && !stepUp.noManagers;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await cashApi.openTill({ float_opening: amount, ...stepUp.creds() });
      await refresh();
      toast.success(`Till open · ${formatCurrency(data.float_opening)} float`);
      onOpened?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(cashError(err, "Couldn't open the till"));
      stepUp.clearPin();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="till-open-dialog">
        <DialogHeader>
          <DialogTitle>Open the till</DialogTitle>
          <DialogDescription>
            Count the float in the drawer before service. {carried}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Opening float</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
              <Input type="number" inputMode="decimal" step="0.01" min="0" value={floatOpening}
                onChange={(e) => setFloatOpening(e.target.value)}
                className="pl-7 h-11 text-xl font-mono-display" data-testid="till-open-float" disabled={submitting} />
            </div>
          </div>
          <ManagerStepUpFields {...stepUp} disabled={submitting} testId="till-open" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={submit} disabled={!canSubmit} data-testid="till-open-confirm">
            {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}Open till
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OpenTillDialog;
